import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { API_CONFIG, type ApiConfig } from '../api-config';

export interface StaffNotificationItem {
  id: string;
  title: string;
  detail: string;
  amount: string;
  date: string;
  timeLabel: string;
  state: 'new' | 'none';
  tone: 'neutral' | 'danger' | 'success';
}

export interface StaffInboxSnapshot {
  unreadCount: number;
  items: StaffNotificationItem[];
}

export interface StaffConversationPreview {
  id: string;
  name: string;
  messagePreview: string;
  timeLabel: string;
  avatarUrl: string;
  unreadCount: number;
}

export interface ConversationMessage {
  id: string;
  body: string;
  mine: boolean;
  senderName: string;
  timeLabel: string;
}

export interface ConversationThread {
  id: string;
  title: string;
  items: ConversationMessage[];
}

interface ThreadEnvelope {
  success?: boolean;
  data?: {
    id?: string;
    title?: string;
    items?: Array<Partial<ConversationMessage>>;
  };
}

interface InboxEnvelope {
  success?: boolean;
  data?: {
    unreadCount?: number;
    items?: Array<Partial<StaffNotificationItem>>;
  };
}

const EMPTY: StaffInboxSnapshot = { unreadCount: 0, items: [] };
const CHAT_AVATAR = 'images/user_photo.png';

@Injectable({ providedIn: 'root' })
export class StaffInboxService {
  private readonly http = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  list(): Observable<StaffInboxSnapshot> {
    return this.http
      .get<InboxEnvelope>(`${this.apiConfig.apiUrl}/inbox/notifications`)
      .pipe(
        map((res) => this.normalize(res)),
        catchError(() => of(EMPTY)),
      );
  }

  markRead(id: string): Observable<StaffInboxSnapshot> {
    return this.http
      .patch<InboxEnvelope>(
        `${this.apiConfig.apiUrl}/inbox/notifications/${id}/read`,
        {},
      )
      .pipe(map((res) => this.normalize(res)));
  }

  listConversations(): Observable<StaffConversationPreview[]> {
    return this.http
      .get<{
        success?: boolean;
        data?: { items?: Array<Partial<StaffConversationPreview>> };
      }>(`${this.apiConfig.apiUrl}/inbox/conversations`)
      .pipe(
        map((res) => this.normalizeConversations(res)),
        catchError(() => of([])),
      );
  }

  private normalize(res: InboxEnvelope | null): StaffInboxSnapshot {
    if (!res?.success || !res.data) {
      return EMPTY;
    }
    const items = Array.isArray(res.data.items)
      ? res.data.items.map((item) => this.normalizeItem(item)).filter((item) => item.id && item.title)
      : [];
    const unreadCount = Number(res.data.unreadCount);
    return {
      unreadCount: Number.isFinite(unreadCount) ? unreadCount : 0,
      items,
    };
  }

  private normalizeItem(item: Partial<StaffNotificationItem>): StaffNotificationItem {
    const tone = item.tone === 'danger' || item.tone === 'success' ? item.tone : 'neutral';
    return {
      id: (item.id || '').trim(),
      title: (item.title || '').trim(),
      detail: item.detail || '',
      amount: item.amount || '',
      date: item.date || '',
      timeLabel: item.timeLabel || '',
      state: item.state === 'new' ? 'new' : 'none',
      tone,
    };
  }

  private normalizeConversations(res: {
    success?: boolean;
    data?: { items?: Array<Partial<StaffConversationPreview>> };
  } | null): StaffConversationPreview[] {
    if (!res?.success || !Array.isArray(res.data?.items)) {
      return [];
    }
    return res.data.items
      .map((item) => this.normalizeConversation(item))
      .filter((item) => item.id && item.name);
  }

  thread(id: string, kind: 'staff' | 'support' = 'staff'): Observable<ConversationThread> {
    return this.http
      .get<ThreadEnvelope>(this.threadUrl(id, kind))
      .pipe(map((res) => this.normalizeThread(res)));
  }

  send(id: string, body: string, kind: 'staff' | 'support' = 'staff'): Observable<ConversationThread> {
    return this.http
      .post<ThreadEnvelope>(this.threadUrl(id, kind), { body })
      .pipe(map((res) => this.normalizeThread(res)));
  }

  listSupportConversations(): Observable<StaffConversationPreview[]> {
    return this.http
      .get<{
        success?: boolean;
        data?: { items?: Array<Partial<StaffConversationPreview>> };
      }>(`${this.apiConfig.apiUrl}/inbox/support-conversations`)
      .pipe(
        map((res) => this.normalizeConversations(res)),
        catchError(() => of([])),
      );
  }

  private threadUrl(id: string, kind: 'staff' | 'support'): string {
    const root = kind === 'support' ? 'support-conversations' : 'conversations';
    return `${this.apiConfig.apiUrl}/inbox/${root}/${id}/messages`;
  }

  private normalizeThread(res: ThreadEnvelope | null): ConversationThread {
    const data = res?.success ? res.data : undefined;
    const items = Array.isArray(data?.items)
      ? data.items.map((item) => ({
          id: (item.id || '').trim(),
          body: item.body || '',
          mine: item.mine === true,
          senderName: item.senderName || '',
          timeLabel: item.timeLabel || '',
        })).filter((item) => item.id)
      : [];
    return {
      id: (data?.id || '').trim(),
      title: (data?.title || 'Conversación').trim(),
      items,
    };
  }

  private normalizeConversation(
    item: Partial<StaffConversationPreview>,
  ): StaffConversationPreview {
    const unread = Number(item.unreadCount);
    return {
      id: (item.id || '').trim(),
      name: (item.name || '').trim(),
      messagePreview: item.messagePreview || '',
      timeLabel: item.timeLabel || '',
      avatarUrl: (item.avatarUrl || '').trim() || CHAT_AVATAR,
      unreadCount: Number.isFinite(unread) && unread > 0 ? unread : 0,
    };
  }
}
